import { useEffect, useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import './AdminBlog.css';

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(path, { credentials: 'include', ...opts });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.errors?.[0]?.msg || `HTTP ${res.status}`);
  }
  return res.json();
}

// ── Configuração do TinyMCE (fora do componente — objeto estável) ──
const TINY_INIT = {
  height: 480,
  menubar: true,
  plugins: 'autolink lists link image media table code wordcount emoticons fullscreen',
  toolbar:
    'undo redo | styles | bold italic underline strikethrough | ' +
    'alignleft aligncenter alignright alignjustify | ' +
    'bullist numlist | link image media | ' +
    'table emoticons | code fullscreen',
  toolbar_mode: 'sliding',
  contextmenu: false,

  // No celular o toolbar normal não cabe — usamos uma versão enxuta que "quebra linha"
  // em vez de colapsar em um menu "More..." (que mostrava rótulos de texto como "Link"
  // sobrepostos à seleção, parecendo substituir o texto selecionado)
  mobile: {
    menubar: false,
    toolbar_mode: 'wrap',
    toolbar: 'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link image media | code',
  },

  images_upload_url: '/admin/media/upload',
  images_upload_credentials: true,
  automatic_uploads: true,
  paste_data_images: true,
  media_live_embeds: true,
  file_picker_types: 'image media',

  file_picker_callback(callback, _value, meta) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = meta.filetype === 'image' ? 'image/*' : 'video/*,audio/*';

    input.onchange = () => {
      const file = input.files[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('file', file);

      fetch('/admin/media/upload', { method: 'POST', body: fd, credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          if (!data.url) throw new Error('URL inválida');
          if (meta.filetype === 'image') {
            callback(data.url, { alt: file.name });
          } else {
            const tag = file.type.startsWith('video/') ? 'video' : 'audio';
            window.tinymce.activeEditor.insertContent(
              `<${tag} controls src="${data.url}" style="max-width:100%;display:block;margin:12px 0"></${tag}>`
            );
          }
        })
        .catch(err => {
          window.tinymce?.activeEditor?.notificationManager?.open({
            text: err.message || 'Erro no upload',
            type: 'error',
          });
        });
    };
    input.click();
  },

  images_upload_handler(blobInfo, progress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/admin/media/upload');
      xhr.withCredentials = true;
      xhr.upload.onprogress = e => progress(Math.round((e.loaded / e.total) * 100));
      xhr.onload = () => {
        if (xhr.status !== 200) { reject(`HTTP ${xhr.status}`); return; }
        try {
          const json = JSON.parse(xhr.responseText);
          const url = json.url || json.location;
          url ? resolve(url) : reject('Resposta inválida');
        } catch { reject('Resposta inválida'); }
      };
      xhr.onerror = () => reject('Erro de rede');
      const fd = new FormData();
      fd.append('file', blobInfo.blob(), blobInfo.filename());
      xhr.send(fd);
    });
  },

  content_style: 'body { font-family: Poppins, sans-serif; font-size: 15px; line-height: 1.7; }',
};

export default function AdminBlog() {
  const [posts,      setPosts]      = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [formOpen,   setFormOpen]   = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [form,       setForm]       = useState({ title: '', category_id: '' });
  const [saving,     setSaving]     = useState(false);
  const [confirmId,  setConfirmId]  = useState(null);
  const [toast,      setToast]      = useState(null);

  // Controle do editor
  const editorRef        = useRef(null);
  const [editorKey,      setEditorKey]      = useState(0);
  const [initialContent, setInitialContent] = useState('');

  // Categorias
  const [catInput,    setCatInput]    = useState('');
  const [savingCat,   setSavingCat]   = useState(false);
  const [catFormOpen, setCatFormOpen] = useState(false);

  const toastTimer = useRef(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }

  async function loadAll() {
    setLoading(true);
    try {
      const [postsData, catsData] = await Promise.all([
        apiFetch('/admin/posts/list'),
        apiFetch('/admin/categories/list'),
      ]);
      setPosts(postsData);
      setCategories(catsData);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  function openCreate() {
    setForm({ title: '', category_id: categories[0]?.id || '' });
    setEditingId(null);
    setInitialContent('');
    setEditorKey(k => k + 1);
    setFormOpen(true);
  }

  async function openEdit(id) {
    try {
      const post = await apiFetch(`/admin/posts/${id}`);
      setForm({ title: post.title, category_id: post.category_id || '' });
      setEditingId(id);
      setInitialContent(post.content || '');
      setEditorKey(k => k + 1);
      setFormOpen(true);
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  function closeForm() {
    setFormOpen(false);
    setEditingId(null);
    setInitialContent('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.category_id) {
      showToast('Preencha título e categoria.', 'error');
      return;
    }
    const content = editorRef.current?.getContent() || '';
    if (!content.trim()) {
      showToast('Adicione algum conteúdo ao post.', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await apiFetch(`/admin/posts/update/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: form.title, content, category_id: form.category_id }),
        });
        showToast('Post atualizado!');
      } else {
        await apiFetch('/admin/posts/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: form.title, content, category_id: form.category_id }),
        });
        showToast('Post criado!');
      }
      closeForm();
      loadAll();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function deletePost(id) {
    try {
      await apiFetch(`/admin/delete/${id}`, { method: 'DELETE' });
      setConfirmId(null);
      showToast('Post excluído.');
      loadAll();
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  async function createCategory(e) {
    e.preventDefault();
    if (!catInput.trim()) return;
    setSavingCat(true);
    try {
      await apiFetch('/admin/categories/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: catInput.trim() }),
      });
      setCatInput('');
      setCatFormOpen(false);
      showToast('Categoria criada!');
      loadAll();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSavingCat(false);
    }
  }

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="ab-page">

      {/* Header */}
      <div className="ab-header">
        <h1><i className="fas fa-pen-nib" /> Gerenciar Blog</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="ab-btn ab-btn-outline" onClick={() => setCatFormOpen(o => !o)}>
            <i className="fas fa-tags" /> Categorias
          </button>
          <button className="ab-btn ab-btn-primary" onClick={openCreate}>
            <i className="fas fa-plus" /> Novo Post
          </button>
        </div>
      </div>

      {/* Category mini form */}
      {catFormOpen && (
        <div className="ab-form-panel" style={{ marginBottom: 20, padding: 20 }}>
          <p style={{ fontWeight: 700, color: '#475569', marginBottom: 12, fontSize: '0.9rem' }}>
            <i className="fas fa-tags" style={{ marginRight: 6, color: '#4f46e5' }} />
            Categorias existentes
          </p>
          <div className="ab-cats-row" style={{ marginBottom: 14 }}>
            {categories.map(c => (
              <span key={c.id} className="ab-cat-chip">{c.name}</span>
            ))}
            {categories.length === 0 && (
              <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Nenhuma categoria.</span>
            )}
          </div>
          <form onSubmit={createCategory} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Nome da nova categoria"
              value={catInput}
              onChange={e => setCatInput(e.target.value)}
              required
              style={{ flex: 1, border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontFamily: 'inherit', outline: 'none' }}
            />
            <button type="submit" className="ab-btn ab-btn-primary" disabled={savingCat}>
              {savingCat ? 'Salvando...' : 'Criar'}
            </button>
          </form>
        </div>
      )}

      {/* Form panel */}
      {formOpen && (
        <div className="ab-form-panel">
          <div className="ab-form-title">
            <i className={editingId ? 'fas fa-edit' : 'fas fa-plus-circle'} />
            {editingId ? 'Editar Post' : 'Novo Post'}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="ab-form-row">
              {/* Título */}
              <div className="ab-field">
                <label>Título <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  placeholder="Título do post"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  required
                />
              </div>

              {/* Categoria */}
              <div className="ab-field">
                <label>Categoria <span style={{ color: '#ef4444' }}>*</span></label>
                <select
                  value={form.category_id}
                  onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
                  required
                >
                  <option value="">Selecione...</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Editor TinyMCE */}
            <div className="ab-editor-wrap">
              <label>Conteúdo <span style={{ color: '#ef4444' }}>*</span></label>
              <Editor
                key={editorKey}
                apiKey="p9z4yi901d4get0ldk3pg6qii38llkc1s3sn3yfc38sql7p5"
                onInit={(_evt, editor) => { editorRef.current = editor; }}
                initialValue={initialContent}
                init={TINY_INIT}
              />
            </div>

            <div className="ab-form-actions">
              <button type="button" className="ab-btn ab-btn-secondary" onClick={closeForm}>
                Cancelar
              </button>
              <button type="submit" className="ab-btn ab-btn-primary" disabled={saving}>
                <i className={`fas fa-${saving ? 'spinner fa-spin' : 'check'}`} />
                {saving ? 'Salvando...' : 'Salvar Post'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Posts list */}
      {loading ? (
        <div className="ab-empty">
          <i className="fas fa-spinner fa-spin" />
          <p>Carregando posts...</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="ab-empty">
          <i className="fas fa-inbox" />
          <p>Nenhum post encontrado. Clique em "Novo Post" para começar.</p>
        </div>
      ) : (
        posts.map(post => (
          <div key={post.id} className="ab-post-card">
            <div className="ab-post-info">
              <div className="ab-post-title">{post.title}</div>
              <div className="ab-post-meta">
                {post.category_name && (
                  <span className="ab-post-cat">
                    <i className="fas fa-tag" style={{ marginRight: 4 }} />{post.category_name}
                  </span>
                )}
                <span><i className="fas fa-calendar" style={{ marginRight: 4 }} />{formatDate(post.created_at)}</span>
                {post.category_type === 'video' && (
                  <span style={{ color: '#ef4444' }}>
                    <i className="fas fa-play-circle" style={{ marginRight: 4 }} />Vídeo
                  </span>
                )}
              </div>
            </div>
            <div className="ab-post-actions">
              <button className="ab-btn ab-btn-warning" onClick={() => openEdit(post.id)}>
                <i className="fas fa-edit" /> Editar
              </button>
              <button className="ab-btn ab-btn-danger" onClick={() => setConfirmId(post.id)}>
                <i className="fas fa-trash" /> Excluir
              </button>
            </div>
          </div>
        ))
      )}

      {/* Confirm delete modal */}
      {confirmId && (
        <div className="ab-modal-backdrop">
          <div className="ab-modal">
            <h3>Excluir post?</h3>
            <p>Esta ação não pode ser desfeita.</p>
            <div className="ab-modal-actions">
              <button className="ab-btn ab-btn-secondary" onClick={() => setConfirmId(null)}>
                Cancelar
              </button>
              <button className="ab-btn ab-btn-danger" onClick={() => deletePost(confirmId)}>
                <i className="fas fa-trash" /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <div className={`ab-toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
